/**
 * spreadsheetHtmlToPlate — converts genuine spreadsheet (Excel/Google Sheets)
 * clipboard HTML into a single Plate table node.
 *
 * Only invoked when `isSpreadsheetTableHtml` fires (see useClipboardHandlers);
 * document-sourced tables (Word, Google Docs, other rich-text editors) fall
 * through to the generic paste path instead, which preserves in-cell lists
 * and other document-level structure this converter doesn't handle.
 *
 * Covers:
 *  - Grid rectangularization: walks <tr>/<td> into a rectangular logical grid
 *    before emitting rows, so ragged/short rows still produce a valid table.
 *  - rowspan/colspan handling: expands spanned cells into placeholder cells
 *    at every occupied grid slot.
 *  - colSizes derivation: reads <colgroup><col> widths (px) into the table
 *    node's colSizes array.
 *  - Cell-align fidelity: reads text-align from the <td>/<th> or its inner
 *    <p>/<div>, carried onto each paragraph's `align` property.
 */
export function isSpreadsheetTableHtml(html: string): boolean {
    if (!/<table[^>]*>/i.test(html)) return false;
    if (/<google-sheets-html-origin/i.test(html)) return true;
    if (/td\.xl\d+/i.test(html)) return true;
    if (/<table[^>]*\sclass=["']ExcelTable/i.test(html)) return true;
    if (/\sdata-sheets-value=/i.test(html)) return true;
    // Excel-only clipboard markers that appear WITHOUT the td.xlNN <style> block
    // (modern Excel, Excel-for-Web, legacy 2003/2007). A single-row copy often
    // omits that block, so detection used to fail and the paste fell to the
    // generic path that collapses a one-row table into a single cell. These
    // markers never occur in Google Docs / Word / generic document HTML, so
    // those keep routing to the generic path (preserving in-cell lists).
    if (/content\s*=\s*["']?Excel\.Sheet/i.test(html)) return true;
    if (/xmlns:x\s*=\s*["']urn:schemas-microsoft-com:office:excel/i.test(html)) return true;
    return false;
}

export function isTableHtml(html: string): boolean {
    return /<table[^>]*>/i.test(html) && /<t[dh][^>]*>/i.test(html);
}

// True when the clipboard HTML is essentially a single table with no other
// meaningful content next to it — the case the spreadsheet shortcut handler
// is built for. Pasting from a previous Slate-based editor (and other "rich
// document" sources) brings headings, paragraphs, AND tables in one blob; for
// those the generic deserialize path is correct. `spreadsheetHtmlToPlateTable`
// would otherwise collapse the entire paste down to just the first table.
export function isPureTableHtml(html: string): boolean {
    if (!isTableHtml(html)) return false;
    if (typeof DOMParser === 'undefined') return false;
    try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const body = doc?.body;
        if (!body) return false;
        const tables = body.querySelectorAll('table');
        if (tables.length !== 1) return false;
        // Strip the table and see whether any visible text content remains.
        const clone = body.cloneNode(true) as HTMLElement;
        clone.querySelectorAll('table').forEach((t) => t.remove());
        const remaining = (clone.textContent ?? '').replace(/&nbsp;/g, ' ').trim();
        return remaining.length === 0;
    } catch {
        return false;
    }
}

// Walk a DOM node and produce Plate inline children (text with marks).
// <BR> elements are NOT emitted here — callers that want line-break handling
// (i.e. cellBlocks) split on <BR> at the block level instead.
function inlineChildren(node: Node): any[] {
    const results: any[] = [];
    for (const child of Array.from(node.childNodes)) {
        if (child.nodeType === Node.TEXT_NODE) {
            const t = child.textContent ?? '';
            if (t) results.push({ text: t });
            continue;
        }
        if (child.nodeType !== Node.ELEMENT_NODE) continue;
        const el = child as HTMLElement;
        const tag = el.tagName;

        // BR is intentionally skipped here. cellBlocks splits on BR at the
        // block level so each segment becomes its own paragraph, which is
        // structurally correct and avoids stray \n text leaves.
        if (tag === 'BR') continue;

        const nested = inlineChildren(el);
        if (nested.length === 0) continue;

        // Apply marks based on tag.
        const marks: Record<string, boolean | string> = {};
        if (tag === 'B' || tag === 'STRONG') marks.bold = true;
        if (tag === 'I' || tag === 'EM') marks.italic = true;
        if (tag === 'U') marks.underline = true;
        if (tag === 'S' || tag === 'DEL' || tag === 'STRIKE') marks.strikethrough = true;
        if (tag === 'CODE') marks.code = true;

        // Read marks from inline style too (Google Docs / Excel express formatting
        // via `style`, not tags). Mirrors the cell-level fidelity of the generic
        // deserialize path so a styled run inside a spreadsheet cell keeps the
        // same leaf marks it would outside one.
        const style = el.getAttribute('style') || '';
        if (/font-weight:\s*(bold|[7-9]\d\d)/i.test(style)) marks.bold = true;
        if (/font-style:\s*italic/i.test(style)) marks.italic = true;
        if (/text-decoration(?:-line)?:[^;]*underline/i.test(style)) marks.underline = true;
        const color = style.match(/(?:^|;)\s*color:\s*([^;]+)/i)?.[1]?.trim();
        if (color) marks.color = color;
        const fontSize = style.match(/font-size:\s*([^;]+)/i)?.[1]?.trim();
        if (fontSize) marks.fontSize = fontSize;

        if (Object.keys(marks).length > 0) {
            for (const leaf of nested) {
                if (typeof leaf.text === 'string') {
                    results.push({ ...marks, ...leaf });
                } else {
                    results.push(leaf);
                }
            }
        } else {
            results.push(...nested);
        }
    }
    return results.length > 0 ? results : [{ text: '' }];
}

// Read text-align from an element's `align` attribute or inline style.
// Excel emits align="right" / Sheets emits style="text-align:right" on the <td>.
// Returns undefined for the default (left) so we don't stamp redundant align.
function readAlign(el: Element): 'right' | 'center' | 'justify' | undefined {
    const attr = (el.getAttribute('align') || '').toLowerCase();
    const style = (el as HTMLElement).getAttribute?.('style') || '';
    const fromStyle = style.match(/text-align:\s*(left|right|center|justify)/i)?.[1]?.toLowerCase();
    // Inline text-align wins over the legacy align attribute when both are set.
    const value = fromStyle || attr;
    return value === 'right' || value === 'center' || value === 'justify' ? value : undefined;
}

// Split a DOM node's child nodes into segments delimited by <BR> elements.
// Each segment is an array of nodes that form one line of content — including
// EMPTY segments between two adjacent <BR>s, which represent an intentional
// blank line (e.g. "A<br><br>B" must keep the blank paragraph between A and
// B). The only segment dropped is a single trailing empty one produced by a
// line-ending <BR> with nothing after it (e.g. "A<br>") — that's just how a
// line terminates, not an intentional extra blank line.
function splitOnBr(node: Element): Node[][] {
    const segments: Node[][] = [];
    let current: Node[] = [];
    for (const child of Array.from(node.childNodes)) {
        if (child.nodeType === Node.ELEMENT_NODE && (child as HTMLElement).tagName === 'BR') {
            segments.push(current);
            current = [];
        } else {
            current.push(child);
        }
    }
    segments.push(current);
    if (segments.length > 1 && segments[segments.length - 1].length === 0) {
        segments.pop();
    }
    return segments;
}

// Produce inline children from an array of DOM nodes (one BR-split segment).
function inlineChildrenFromNodes(nodes: Node[]): any[] {
    // Wrap in a temporary fragment-like container to reuse inlineChildren.
    // We call inlineChildren on each node individually and concatenate.
    const results: any[] = [];
    for (const node of nodes) {
        if (node.nodeType === Node.TEXT_NODE) {
            const t = node.textContent ?? '';
            if (t) results.push({ text: t });
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            // Reuse inlineChildren for element nodes by wrapping in a dummy span.
            // Instead, call inlineChildren on the element directly (it handles marks).
            const leaves = inlineChildren(node);
            // inlineChildren returns [{text:''}] for empty nodes — skip those.
            if (!(leaves.length === 1 && leaves[0].text === '')) {
                results.push(...leaves);
            }
        }
    }
    return results.length > 0 ? results : [{ text: '' }];
}

// Convert a cell's inner DOM into Plate paragraph blocks. Cell text-align
// (from the <td>, or an inner <p>/<div> which takes precedence) is carried onto
// the paragraph node's `align` — the same property the toolbar AlignMenu sets —
// so a right-aligned spreadsheet column stays right-aligned after paste.
//
// <BR> elements inside a cell are treated as paragraph separators: the content
// before and after each <BR> becomes its own `p` block. This prevents stray
// `{text:'\n'}` leaves from appearing inside paragraph children.
function cellBlocks(td: Element): any[] {
    const blocks: any[] = [];
    const cellAlign = readAlign(td);

    // If the cell has <p> or <div> children, treat each as a separate paragraph.
    // Each child paragraph may itself contain <BR>s — split those too.
    const paras = Array.from(td.querySelectorAll(':scope > p, :scope > div'));
    if (paras.length > 0) {
        for (const p of paras) {
            const align = readAlign(p) ?? cellAlign;
            // Check whether this paragraph contains any direct <BR> children.
            const hasBr = Array.from(p.childNodes).some((c) => c.nodeType === Node.ELEMENT_NODE && (c as HTMLElement).tagName === 'BR');
            if (hasBr) {
                const segments = splitOnBr(p);
                for (const seg of segments) {
                    const children = inlineChildrenFromNodes(seg);
                    blocks.push({ type: 'p', children, ...(align ? { align } : {}) });
                }
            } else {
                blocks.push({ type: 'p', children: inlineChildren(p), ...(align ? { align } : {}) });
            }
        }
        return blocks;
    }

    // Fallback: the cell has no block-level children. Split on top-level <BR>s
    // to produce one paragraph per line segment.
    const hasBr = Array.from(td.childNodes).some((c) => c.nodeType === Node.ELEMENT_NODE && (c as HTMLElement).tagName === 'BR');
    if (hasBr) {
        const segments = splitOnBr(td);
        for (const seg of segments) {
            const children = inlineChildrenFromNodes(seg);
            blocks.push({ type: 'p', children, ...(cellAlign ? { align: cellAlign } : {}) });
        }
    } else {
        blocks.push({ type: 'p', children: inlineChildren(td), ...(cellAlign ? { align: cellAlign } : {}) });
    }

    return blocks;
}

// Parse a px width from a <col> element's style or width attribute.
// Returns 0 when no usable positive width is present.
function colPxWidth(col: Element): number {
    const styleWidth = (col as HTMLElement).style?.width || '';
    const fromStyle = parseInt(styleWidth, 10);
    if (Number.isFinite(fromStyle) && fromStyle > 0) return fromStyle;
    const attrWidth = parseInt(col.getAttribute('width') || '', 10);
    if (Number.isFinite(attrWidth) && attrWidth > 0) return attrWidth;
    return 0;
}

// Parse colspan / rowspan from a td/th element. Returns 1 when absent or invalid.
function spanAttr(td: Element, attr: 'colspan' | 'rowspan'): number {
    const v = parseInt(td.getAttribute(attr) || '1', 10);
    return Number.isFinite(v) && v >= 1 ? v : 1;
}

export function spreadsheetHtmlToPlateTable(html: string): any | null {
    if (typeof DOMParser === 'undefined') return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const table = doc.querySelector('table');
    if (!table) return null;

    const trs = Array.from(table.querySelectorAll('tr'));
    if (trs.length === 0) return null;

    // ── Phase 1: build a rectangular logical grid ────────────────────────────
    // The grid maps (rowIndex, colIndex) → { td element, isSpanFill }.
    // `isSpanFill` marks cells that are occupied by a rowspan/colspan expansion
    // of a real cell rather than a new <td>/<th>. Those positions get empty
    // placeholder cells in the final Plate output.

    // First pass: determine grid dimensions by simulating the span layout.
    const rowCount = trs.length;
    // occupancy[r][c] = true once a cell claims that slot (via span expansion).
    const occupancy: boolean[][] = Array.from({ length: rowCount }, () => []);

    // Record the real cell at each grid slot.
    type GridCell = { el: Element; isPlaceholder: false } | { el: null; isPlaceholder: true };
    const grid: GridCell[][] = Array.from({ length: rowCount }, () => []);

    for (let r = 0; r < rowCount; r++) {
        const tr = trs[r];
        const tdEls = Array.from(tr.querySelectorAll(':scope > td, :scope > th'));
        let cellIdx = 0; // index into tdEls for the current row

        // Walk columns, skipping already-occupied slots (from prior rowspans).
        let c = 0;
        while (cellIdx < tdEls.length) {
            // Skip slots already occupied by a rowspan from a previous row.
            while (occupancy[r]?.[c]) c++;

            const td = tdEls[cellIdx];
            const colspan = spanAttr(td, 'colspan');
            const rowspan = spanAttr(td, 'rowspan');

            // Mark all slots this cell spans as occupied.
            for (let dr = 0; dr < rowspan; dr++) {
                for (let dc = 0; dc < colspan; dc++) {
                    const row = r + dr;
                    const col = c + dc;
                    if (row < rowCount) {
                        if (!occupancy[row]) occupancy[row] = [];
                        occupancy[row][col] = true;
                    }
                }
            }

            // Place the real cell at (r, c).
            grid[r][c] = { el: td, isPlaceholder: false };

            // Fill the spanned columns in this row with placeholders.
            for (let dc = 1; dc < colspan; dc++) {
                grid[r][c + dc] = { el: null, isPlaceholder: true };
            }
            // Fill the spanned rows (and their spanned columns) with placeholders.
            for (let dr = 1; dr < rowspan; dr++) {
                for (let dc = 0; dc < colspan; dc++) {
                    const row = r + dr;
                    if (row < rowCount) {
                        grid[row][c + dc] = { el: null, isPlaceholder: true };
                    }
                }
            }

            c += colspan;
            cellIdx++;
        }
        // Advance past any trailing rowspan-occupied slots in this row.
        // (No action needed — grid[r] already has all real cells placed.)
    }

    // Compute the actual column count from the grid (maximum row width).
    let columnCount = 0;
    for (let r = 0; r < rowCount; r++) {
        const rowWidth = grid[r].length;
        if (rowWidth > columnCount) columnCount = rowWidth;
    }
    if (columnCount === 0) return null;

    // ── Phase 2: build Plate rows, padding each row to columnCount ───────────
    const emptyCell = (): any => ({ type: 'td', children: [{ type: 'p', children: [{ text: '' }] }] });

    const rows = grid.map((gridRow) => {
        const cells: any[] = [];
        for (let c = 0; c < columnCount; c++) {
            const slot = gridRow[c];
            if (!slot || slot.isPlaceholder) {
                // Rowspan/colspan expansion placeholder — emit an empty cell.
                cells.push(emptyCell());
            } else {
                const td = slot.el!;
                cells.push({
                    type: td.tagName === 'TH' ? 'th' : 'td',
                    children: cellBlocks(td),
                });
            }
        }
        return {
            type: 'tr',
            children: cells.length ? cells : [emptyCell()],
        };
    });

    const node: any = { type: 'table', children: rows };

    // ── Phase 3: build colSizes from <col> elements ──────────────────────────
    // columnCount is now the correct logical column count from the span-expanded
    // grid, so colSizes will always have exactly columnCount entries.
    const cols = Array.from(table.querySelectorAll('colgroup > col'));
    if (cols.length > 0) {
        const widths = cols.map(colPxWidth);
        if (widths.some((w) => w > 0)) {
            node.colSizes = Array.from({ length: columnCount }, (_, i) => widths[i] ?? 0);
        }
    }

    return node;
}
